import { createContext, useContext, useState, type ReactNode } from "react";
import type { TaskResults } from "../pages/qa-results-new/QAResultsNew";

// Types
export interface QATaskState {
  taskId: string | null;
  status: "idle" | "processing" | "completed" | "failed";
  results: any | null;
  error: string | null;
}

interface QATaskContextType {
  taskState: QATaskState;
  setTaskState: (state: QATaskState) => void;
  setTaskId: (taskId: string) => void;
  setStatus: (status: QATaskState["status"]) => void;
  setResults: (results: any) => void;
  setError: (error: string) => void;
  taskResults: TaskResults | null;
  setTaskResults: (results: TaskResults | null) => void;
  resetTask: () => void;
  taskCreated: string;
  setTaskCreated: (new_task_id: string) => void;
}

const QATaskContext = createContext<QATaskContextType | undefined>(undefined);

interface QATaskProviderProps {
  children: ReactNode;
}

const initialState: QATaskState = {
  taskId: null,
  status: "idle",
  results: null,
  error: null,
};

export const QATaskProvider: React.FC<QATaskProviderProps> = ({ children }) => {
  const [taskState, setTaskState] = useState<QATaskState>(initialState);
  const [taskResults, setTaskResults] = useState<TaskResults | null>(null);
  const [taskCreated, setTaskCreated] = useState<string>("");
  const setTaskId = (taskId: string) => {
    setTaskState((prev) => ({
      ...prev,
      taskId,
      status: "processing",
      error: null,
    }));
  };

  const setStatus = (status: QATaskState["status"]) => {
    setTaskState((prev) => ({ ...prev, status }));
  };

  const setResults = (results: any) => {
    setTaskState((prev) => ({
      ...prev,
      results,
      status: "completed",
    }));
  };

  const setError = (error: string) => {
    setTaskState((prev) => ({
      ...prev,
      error,
      status: "failed",
    }));
  };

  const resetTask = () => {
    setTaskState(initialState);
    setTaskCreated("");
  };

  return (
    <QATaskContext.Provider
      value={{
        taskState,
        setTaskState,
        setTaskId,
        setStatus,
        setResults,
        setError,
        resetTask,
        taskResults,
        setTaskResults,
        taskCreated,
        setTaskCreated,
      }}
    >
      {children}
    </QATaskContext.Provider>
  );
};

export const useQATask = (): QATaskContextType => {
  const context = useContext(QATaskContext);
  if (!context) {
    throw new Error("useQATask must be used within QATaskProvider");
  }
  return context;
};
